import React from 'react';

import { cn } from '@eluve/components';

/**
 * Dependency-light inline sparkline for a measure's series (ENG-5366
 * stretch). Solid polyline through validated points; a pending AI draft
 * appends as a dashed segment ending in a hollow point; reviewer-amended
 * points carry a halo ring. Single-color by design — everything draws in
 * `currentColor`, so the consumer picks the tone via text color classes.
 */
export type SparklinePoint = {
  value: number;
  /** Reviewer corrected this value — rendered with a halo ring. */
  amended?: boolean;
};

export type TrendSparklineProps = {
  /** Validated points, oldest first. */
  points: SparklinePoint[];
  /** A pending draft value, appended after the last validated point. */
  proposedValue?: number;
  className?: string;
};

const WIDTH = 88;
const HEIGHT = 22;
const PAD = 4;

export const TrendSparkline: React.FC<TrendSparklineProps> = ({
  points,
  proposedValue,
  className,
}) => {
  const total = points.length + (proposedValue === undefined ? 0 : 1);
  if (total < 2) return null;

  const values = points.map((p) => p.value);
  if (proposedValue !== undefined) values.push(proposedValue);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const x = (index: number) => PAD + index * ((WIDTH - 2 * PAD) / (total - 1));
  const y = (value: number) =>
    PAD + ((max - value) / span) * (HEIGHT - 2 * PAD);

  const lastSolidPoint = points[points.length - 1];

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      fill="none"
      className={cn('block overflow-visible privacy-text', className)}
      role="img"
      aria-label="Value trend"
    >
      {points.length >= 2 && (
        <polyline
          points={points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ')}
          stroke="currentColor"
          strokeWidth={1}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      )}
      {proposedValue !== undefined && lastSolidPoint && (
        <line
          x1={x(points.length - 1)}
          y1={y(lastSolidPoint.value)}
          x2={x(points.length)}
          y2={y(proposedValue)}
          stroke="currentColor"
          strokeWidth={1}
          strokeDasharray="2.5 2"
        />
      )}
      {points.map((point, index) => {
        const isLast =
          index === points.length - 1 && proposedValue === undefined;
        return (
          <React.Fragment key={index}>
            <circle
              cx={x(index)}
              cy={y(point.value)}
              r={isLast ? 1.9 : 1.3}
              fill="currentColor"
            />
            {point.amended && (
              <circle
                cx={x(index)}
                cy={y(point.value)}
                r={3.4}
                fill="none"
                stroke="currentColor"
                strokeWidth={0.8}
                opacity={0.55}
              />
            )}
          </React.Fragment>
        );
      })}
      {proposedValue !== undefined && (
        <circle
          data-testid="sparkline-proposed"
          cx={x(points.length)}
          cy={y(proposedValue)}
          r={2.2}
          className="fill-bg-primary"
          stroke="currentColor"
          strokeWidth={1.4}
        />
      )}
    </svg>
  );
};
